import React, { createContext, useEffect, useState } from 'react';
import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useLazyQuery, useQuery } from '@apollo/client';

import { NeighborhoodWithoutId } from '../../../apolloClient/types/Neighborhood';
import BuildingPage from '../../../components/Building/BuildingPage';

import { Building, EntityWithId } from 'apolloClient/types';
import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from 'lib/flattenStrapiBulkDataItems';
import { BlogsResponse, BlogWithoutId } from 'apolloClient/types/Blog';
import { BuildingPageContextInterface } from 'apolloClient/types/Building';
import {
  BUILDING_AND_BLOGS_BY_SLUG,
  BUILDING_EXISTS_BY_SLUG,
  GET_BUILDING_LISTING_COUNTS,
  GET_IS_BUILDING_FAVS,
  NEARBY_CONDOS,
  NEARBY_CONDOS_WITHOUT_RANGES,
} from 'apolloClient/queries/building';
import NotFoundPage from 'components/NotFoundPage';
import { useStaleSlugGuard } from 'hooks/useStaleSlugGuard';
import { client } from 'apolloClient/client';
import { queryWithRetry } from 'lib/queryWithRetry';
import { ListingCollectionResponse } from 'apolloClient/types/Units';
import { getNearbyCondosParams } from 'lib/nearbyCondosParams';
import { notfoundRedirect } from 'constants/notFoundParams';
import { ResponseCollection } from 'apolloClient/types/common';
import { DEFAULT_REVALIDATE_INTERVAL, ORIGIN } from 'src/constants';
import { useAuth, useOnLoggedIn } from 'components/Auth/AuthProvider';
import { updateListingFavoritesQuery } from 'src/utils/updateListingFavoritesQuery';
import BuildingProductSchema from 'components/Schema/BuildingProductSchema';
import BuildingAppartmentComplexSchema from 'components/Schema/BuildingAppartmentComplexSchema';
import { getThreeUniquePageBlogs } from 'src/utils/blogs';
import { useFavoriteBuilding } from 'components/SavedHouses/SavedFavoritesBuildingsContext';
import { useFavorite } from 'components/SavedHouses/SavedFavoritesContext';

interface BuildingComponentTypes {
  building: Building;
  neighborhood: EntityWithId<NeighborhoodWithoutId>;
  blogs: EntityWithId<BlogWithoutId>[];
  saleCount: number;
  rentCount: number;
}

export const BuildingPageContext =
  createContext<BuildingPageContextInterface | null>(null);

// Outer guard: if the building's (or its neighborhood's) slug changed, the old
// URL keeps serving this stale cached page. Re-check existence client-side and
// 404 BEFORE mounting the page, so a stale page fires no building queries/effects.
const BuildingComponent: React.FC<BuildingComponentTypes> = (props) => {
  const isStalePage = useStaleSlugGuard<{ buildings?: { data?: unknown[] } }>(
    BUILDING_EXISTS_BY_SLUG,
    { slug: props.building.slug, nhSlug: props.neighborhood.slug },
    (d) => Boolean(d?.buildings?.data?.length)
  );

  if (isStalePage) {
    return <NotFoundPage />;
  }

  return <BuildingContent {...props} />;
};

const BuildingContent: React.FC<BuildingComponentTypes> = (props) => {
  const { building, neighborhood, blogs, rentCount, saleCount } = props;
  const { asPath } = useRouter();

  const buildingStats = building?.buildingStats;

  const [nearbyCondosParams, setNearbyCondosParams] = useState(
    getNearbyCondosParams({
      minAvgCoef: 0.5,
      maxAvgCoef: 1.5,
      buildingStats,
    })
  );

  const { data: liveCountsData } = useQuery<{
    saleCount: ListingCollectionResponse;
    rentCount: ListingCollectionResponse;
  }>(GET_BUILDING_LISTING_COUNTS, {
    variables: { slug: building.slug, nhSlug: neighborhood.slug },
    fetchPolicy: 'network-only',
  });

  const liveSaleCount =
    liveCountsData?.saleCount?.meta?.pagination?.total ?? saleCount;
  const liveRentCount =
    liveCountsData?.rentCount?.meta?.pagination?.total ?? rentCount;

  const { getActualAuthData } = useAuth();
  const { latestAction: latestActionBuilding } = useFavoriteBuilding();
  const { latestAction } = useFavorite();
  const [favorite, setFavorite] = useState(building.favorite);

  const [
    fetchNearby,
    {
      data: nearbyCondosData,
      refetch: refetchNearbyCondos,
      updateQuery: updateNearbyQuery,
      error: nearbyCondosError,
    },
  ] = useLazyQuery<{
    listings: ListingCollectionResponse;
  }>(NEARBY_CONDOS, {
    variables: {
      neighborhoodId: neighborhood.id,
      ...nearbyCondosParams,
      buildingId: building.id,
      propertyTypeList: ['C', 'RI', 'S', 'R'],
    },
  });

  const [
    fetchNearbyWithoutRanges,
    {
      data: nearbyCondosWithoutRangesData,
      refetch: refetchNearbyWithoutRanges,
      updateQuery: updateNearbyWithoutRangesQuery,
    },
  ] = useLazyQuery<{ listings: ListingCollectionResponse }>(
    NEARBY_CONDOS_WITHOUT_RANGES,
    {
      variables: {
        neighborhoodId: neighborhood.id,
        buildingId: building.id,
        propertyTypeList: ['C', 'RI', 'S', 'R'],
      },
    }
  );

  const [fetchIsfavs, { data: favoritesData }] = useLazyQuery<
    {
      favoriteBuildings: {
        data: [
          {
            id: number;
          }
        ];
      };
    },
    {
      userId: number;
      buildingId: number;
    }
  >(GET_IS_BUILDING_FAVS);

  useEffect(() => {
    const authData = getActualAuthData();
    fetchNearby({
      variables: {
        neighborhoodId: neighborhood.id,
        ...nearbyCondosParams,
        buildingId: building.id,
        propertyTypeList: ['C', 'RI', 'S', 'R'],
        userId: authData.user.id || -1,
        includeFavorites: !!authData.user.id,
      },
    });
  }, [building.id]);

  useEffect(() => {
    if (!nearbyCondosError) return;
    const authData = getActualAuthData();
    fetchNearbyWithoutRanges({
      variables: {
        neighborhoodId: neighborhood.id,
        buildingId: building.id,
        propertyTypeList: ['C', 'RI', 'S', 'R'],
        userId: authData.user.id || -1,
        includeFavorites: !!authData.user.id,
      },
    });
    setNearbyCondosParams({ ...nearbyCondosParams, unused: true });
  }, [nearbyCondosError, building.id]);

  useEffect(() => {
    if (!favoritesData) return;
    const { favoriteBuildings } = favoritesData;
    if (!favoriteBuildings.data.length) {
      return setFavorite({ data: [] });
    }
    const favorite = favoriteBuildings.data[0];
    setFavorite({
      data: [
        {
          ...favorite,
          attributes: {
            building: {
              ...building,
              id: building.id,
            },
          },
        },
      ],
    });
  }, [favoritesData]);

  useOnLoggedIn(() => {
    const authData = getActualAuthData();
    if (nearbyCondosParams.unused) {
      return refetchNearbyWithoutRanges({
        userId: authData.user.id || -1,
        includeFavorites: !!authData.user.id,
      });
    }
    refetchNearbyCondos({
      userId: authData.user.id || -1,
      includeFavorites: !!authData.user.id,
    });
    fetchIsfavs({
      variables: {
        buildingId: building.id,
        userId: authData.user.id || -1,
      },
    });
  });

  useEffect(() => {
    if (latestAction) {
      if (nearbyCondosParams.unused) {
        return updateNearbyWithoutRangesQuery(
          updateListingFavoritesQuery(latestAction, 'listings')
        );
      }
      updateNearbyQuery(updateListingFavoritesQuery(latestAction, 'listings'));
    }
  }, [latestAction]);

  useEffect(() => {
    if (latestActionBuilding) {
      if (latestActionBuilding.buildingId === building.id) {
        return setFavorite({
          data:
            latestActionBuilding.action === 'create'
              ? [
                  {
                    id: latestActionBuilding.favoriteId,
                    attributes: {
                      building: {
                        ...building,
                        id: building.id,
                      },
                    },
                  },
                ]
              : [],
        });
      }
    }
  }, [latestActionBuilding]);

  const nearbyCondos = nearbyCondosData?.listings?.data?.length
    ? flattenStrapiBulkDataItems(nearbyCondosData.listings.data)
    : flattenStrapiBulkDataItems(nearbyCondosWithoutRangesData?.listings?.data);

  const showProductSchema =
    building?.buildingStats?.saleHistory || liveSaleCount || liveRentCount;

  return (
    <BuildingPageContext.Provider
      value={{
        building,
        nearbyCondos,
        calculatedParams: nearbyCondosParams,
        blogs: blogs,
        neighborhood,
        rentCount: liveRentCount,
        saleCount: liveSaleCount,
        url: `${ORIGIN}${asPath}`,
        favorite,
        medianPrice:
          building.medianSalePrice || building.medianRentPrice || null,
      }}
    >
      {!!showProductSchema && <BuildingProductSchema />}
      <BuildingAppartmentComplexSchema />
      <BuildingPage />
    </BuildingPageContext.Provider>
  );
};

export default BuildingComponent;

export const getStaticProps: GetStaticProps<
  BuildingComponentTypes,
  { urlBuildParam: string; urlParam: string; id: string }
> = async ({ params }) => {
  const urlParam = params?.urlParam as string;
  const urlBuildParam = params?.urlBuildParam as string;
  const URL = `/${urlParam}/${urlBuildParam}`;
  const start = Date.now();
  console.log(`[BuildingPage] getStaticProps started for ${URL}`);

  try {
    const buildingResult = await queryWithRetry<{
      buildings: ResponseCollection<Building>;
      blogs: BlogsResponse;
      saleCount: ListingCollectionResponse;
      rentCount: ListingCollectionResponse;
    }>(
      {
        query: BUILDING_AND_BLOGS_BY_SLUG,
        variables: {
          slug: urlBuildParam,
          nhSlug: urlParam,
          blogLimit: 3,
          neighborsLimit: 3,
          blogsDateFrom: new Date(
            new Date().setFullYear(new Date().getFullYear() - 2)
          ).toISOString(),
        },
        fetchPolicy: 'no-cache',
      },
      'BuildingPage/building'
    );

    const { buildings, blogs, rentCount, saleCount } = buildingResult?.data;

    if (!buildings || !buildings.data?.length) {
      return notfoundRedirect;
    }

    const building = flattenStrapiDataItem(buildings.data[0]);
    const buildingId = building.id;
    if (!buildingId) return notfoundRedirect;

    const neighborhood = flattenStrapiDataItem(building?.neighborhood?.data);
    if (!neighborhood?.id) return notfoundRedirect;

    const urlIsCorrect =
      urlBuildParam === building?.slug && urlParam === neighborhood.slug;
    if (!urlIsCorrect) return notfoundRedirect;

    const buildingsBlogs = flattenStrapiBulkDataItems(building?.blogs?.data);
    const nhBlogs = flattenStrapiBulkDataItems(neighborhood?.blogs?.data);
    const otherBlogs = flattenStrapiBulkDataItems(blogs?.data);

    const pageBlogs = [...buildingsBlogs, ...nhBlogs, ...otherBlogs];
    const threeUniquePageBlogs = getThreeUniquePageBlogs(pageBlogs);

    console.log(
      `[BuildingPage] getStaticProps done in ${Date.now() - start}ms for ${URL}`
    );
    return {
      props: {
        blogs: threeUniquePageBlogs,
        building,
        neighborhood,
        rentCount: rentCount?.meta?.pagination?.total || 0,
        saleCount: saleCount?.meta?.pagination?.total || 0,
        medianPrice:
          building.medianSalePrice || building.medianRentPrice || null,
      },
      revalidate: DEFAULT_REVALIDATE_INTERVAL,
    };
  } catch (error: any) {
    console.error(
      `[BuildingPage] Error fetching data for /${urlParam}/${urlBuildParam}:`
    );

    if (error?.graphQLErrors?.length) {
      error.graphQLErrors.forEach((gqlError: any, index: number) => {
        console.error(
          `  GraphQL Error ${index + 1}:`,
          JSON.stringify(
            {
              message: gqlError.message,
              extensions: gqlError.extensions,
              path: gqlError.path,
              locations: gqlError.locations,
            },
            null,
            2
          )
        );
      });
    }

    if (error?.networkError) {
      console.error(
        '  Network Error:',
        JSON.stringify(
          {
            message: error.networkError.message,
            statusCode: error.networkError.statusCode,
            result: error.networkError.result,
          },
          null,
          2
        )
      );
    }

    if (!error?.graphQLErrors?.length && !error?.networkError) {
      console.error('  Error:', error?.message || error);
    }

    return notfoundRedirect;
  }
};

export async function getStaticPaths() {
  // We decided to not fetch them to reduce build time.
  // const pageSize = 100;
  // let page = 1;
  // let finished = false;
  // let result: Entity<Building>[] = [];

  // while (!finished) {
  //   const { data } = await client.query<{
  //     buildings: ResponseCollection<Building>;
  //   }>({
  //     query: BUILDING_ITEMS_LIST,
  //     variables: { pageSize, page },
  //     fetchPolicy: 'no-cache', // Ensure data is fetched fresh each time
  //   });

  //   result = result.concat(data.buildings.data);
  //   page += 1;

  //   if (data.buildings.data.length < pageSize) {
  //     finished = true;
  //   }
  // }

  // const paths = result
  //   .filter(
  //     (building) =>
  //       building?.attributes?.neighborhood?.data?.attributes?.slug &&
  //       building?.attributes?.slug
  //   )
  //   .map((building) => ({
  //     params: {
  //       urlBuildParam: building.attributes.slug,
  //       urlParam: building.attributes.neighborhood.data.attributes.slug,
  //     },
  //   }));

  return { paths: [], fallback: 'blocking' };
}
